/**
 * Dynamic Store Manager with Redux-Persist Integration
 *
 * This module implements the proven patterns for making redux-persist work with dynamic reducers:
 * 1. Maintains reference to persistor and calls persistor.persist() after replaceReducer
 * 2. Handles manual rehydration for newly injected reducers
 * 3. Manages async reducers map for proper persistence
 * 4. Provides event-driven architecture for store changes
 */

import { Reducer, combineReducers } from '@reduxjs/toolkit';
import { Persistor, getStoredState, REHYDRATE } from 'redux-persist';
import { persistReducer } from 'redux-persist';
import { StoreWithReducerManager } from './dynamicStore';
import { FeatureModule } from './features/types';
import { createFeaturePersistReducers, FeaturePersistenceConfig } from './features/persistence';
import { persistConfig } from './persistConfig';

// Simple event system for store changes
const eventListeners: { [key: string]: Function[] } = {};

function emit(event: string, ...args: any[]) {
  if (eventListeners[event]) {
    eventListeners[event].forEach(listener => listener(...args));
  }
}

function on(event: string, listener: Function) {
  if (!eventListeners[event]) {
    eventListeners[event] = [];
  }
  eventListeners[event].push(listener);
}

function removeAllListeners() {
  Object.keys(eventListeners).forEach(key => {
    delete eventListeners[key];
  });
}

// Map to track async reducers
let asyncReducers: Record<string, Reducer> = {};

// Map to track reducer persistence configs
let reducerPersistConfigs: Record<string, FeaturePersistenceConfig> = {};

// References to store and persistor
let storeRef: StoreWithReducerManager | null = null;
let persistorRef: Persistor | null = null;

/**
 * Initialize the dynamic persist store manager
 *
 * @param store - Redux store instance
 * @param persistor - Persistor instance (can be null initially for scoped persistence)
 * @param initialReducers - Initial static reducers
 */
export function initializeDynamicPersistStore(
  store: StoreWithReducerManager,
  persistor: Persistor | null,
  initialReducers: Record<string, Reducer>,
) {
  storeRef = store;
  persistorRef = persistor;
  asyncReducers = { ...initialReducers };

  // Listen for reducer changes and re-persist
  on('reducer-injected', handleReducerInjection);

  if (persistor) {
    console.log('🏗️ Dynamic persist store manager initialized with persistor');
  } else {
    console.log('🏗️ Dynamic persist store manager initialized (persistor will be set later)');
  }
}

/**
 * Update the persistor reference after it's been created
 * This is used for scoped persistence where persistor is created after owner is available
 */
export function updatePersistor(persistor: Persistor) {
  persistorRef = persistor;
  console.log('✅ Persistor reference updated in dynamic store manager');
}

/**
 * Create root reducer with all current async reducers
 */
function createRootReducer(): Reducer {
  return combineReducers(asyncReducers);
}

/**
 * Handle reducer injection event
 */
async function handleReducerInjection(reducerKey: string) {
  if (!storeRef) {
    console.error('❌ Store not initialized - cannot inject reducer');
    return;
  }

  console.log(`🔄 Handling reducer injection for: ${reducerKey}`);

  try {
    // Step 1: Replace the root reducer with new combined reducer
    const newRootReducer = createRootReducer();
    const persistedReducer = persistReducer(persistConfig, newRootReducer);

    storeRef.replaceReducer(persistedReducer);
    console.log(`📝 Replaced root reducer with ${Object.keys(asyncReducers).length} reducers`);

    // Step 2: Re-trigger persistence (only if persistor is available)
    if (persistorRef) {
      persistorRef.persist();
      console.log(`💾 Called persistor.persist() for reducer: ${reducerKey}`);

      // Step 3: Manual rehydration for the new reducer (if it has persisted state)
      await rehydrateNewReducer(reducerKey);
    } else {
      console.log(`⏭️  Skipping persistence for ${reducerKey} (persistor not yet initialized)`);
    }
  } catch (error) {
    console.error(`❌ Error handling reducer injection for ${reducerKey}:`, error);
  }
}

/**
 * Manually rehydrate a newly injected reducer
 */
async function rehydrateNewReducer(reducerKey: string) {
  if (!storeRef) return;

  try {
    // Check if there's any persisted state for this reducer
    const persistConfig = reducerPersistConfigs[reducerKey];

    // Skip rehydration if persistence is disabled for this reducer
    if (!persistConfig || !persistConfig.enabled) {
      console.log(`⏭️ Skipping rehydration for ${reducerKey} (persistence disabled)`);
      return;
    }

    if (persistConfig.key) {
      // For nested persistence, check the specific storage key
      const storageKey = `persist:${persistConfig.key}_${reducerKey}`;

      try {
        const storedValue = await persistConfig.storage?.getItem?.(storageKey);

        if (storedValue) {
          console.log(`📥 Found persisted state for ${reducerKey}, triggering rehydration`);

          // Parse and dispatch the stored state
          const parsedState = JSON.parse(storedValue);

          storeRef.dispatch({
            type: REHYDRATE,
            key: persistConfig.key,
            payload: parsedState,
            err: null,
          });
        }
      } catch (error) {
        console.error(`❌ Error reading storage for ${reducerKey}:`, error);
      }
    } else {
      // For root-level persistence, get the full stored state
      const storedState = await getStoredState(persistConfig as any);

      if (storedState && (storedState as any)[reducerKey]) {
        console.log(`📥 Found root persisted state for ${reducerKey}, triggering rehydration`);

        storeRef.dispatch({
          type: REHYDRATE,
          key: persistConfig.key || 'root',
          payload: { [reducerKey]: (storedState as any)[reducerKey] },
          err: null,
        });
      }
    }
  } catch (error) {
    console.error(`❌ Error rehydrating ${reducerKey}:`, error);
  }
}

/**
 * Inject a reducer dynamically with proper persistence handling
 */
export function injectReducer(
  key: string,
  reducer: Reducer,
  persistenceConfig?: FeaturePersistenceConfig,
): boolean {
  if (!storeRef) {
    console.error('❌ Store not initialized. Call initializeDynamicPersistStore first.');
    return false;
  }

  if (asyncReducers[key]) {
    console.log(`⚠️ Reducer ${key} already exists`);
    return false;
  }

  console.log(`🚀 Injecting reducer: ${key}`);

  // Store the persistence config
  if (persistenceConfig) {
    reducerPersistConfigs[key] = persistenceConfig;
  }

  // Apply persistence to the reducer if configured
  let finalReducer = reducer;
  if (persistenceConfig?.enabled) {
    console.log(`🔐 Applying persistence to reducer ${key}`);
    const persistedReducers = createFeaturePersistReducers(
      { [key]: reducer },
      persistenceConfig.key || key,
      persistenceConfig,
    );
    finalReducer = persistedReducers[key];
  }

  // Add to async reducers map
  asyncReducers[key] = finalReducer;

  // Update the store's reducer manager (for compatibility)
  storeRef.reducerManager.add(key, finalReducer);

  // Emit event to trigger persistence handling
  emit('reducer-injected', key);
  return true;
}

/**
 * Inject a feature module dynamically
 */
export function injectFeature(feature: FeatureModule): boolean {
  console.log(`🎯 Injecting feature: ${feature.name} (${feature.id})`);

  let success = true;

  // Inject all feature reducers
  Object.entries(feature.reducers).forEach(([key, reducer]) => {
    const injected = injectReducer(key, reducer, feature.persistence);
    if (!injected) {
      console.error(`❌ Failed to inject reducer ${key} for feature ${feature.id}`);
      success = false;
    }
  });

  // Add middleware if any
  if (
    feature.middleware &&
    feature.middleware.length > 0 &&
    storeRef &&
    storeRef.dynamicMiddleware
  ) {
    feature.middleware.forEach(middleware => {
      storeRef!.dynamicMiddleware.addMiddleware(middleware);
    });
  }

  // Run setup function
  if (feature.setup && storeRef) {
    try {
      feature.setup(storeRef);
      console.log(`✅ Setup completed for feature ${feature.id}`);
    } catch (error) {
      console.error(`❌ Setup failed for feature ${feature.id}:`, error);
      success = false;
    }
  }

  return success;
}

/**
 * Remove a reducer dynamically
 */
export function removeReducer(key: string): boolean {
  if (!storeRef || !asyncReducers[key]) {
    return false;
  }

  console.log(`🗑️ Removing reducer: ${key}`);

  // Remove from async reducers map
  delete asyncReducers[key];
  delete reducerPersistConfigs[key];

  // Update the store
  storeRef.reducerManager.remove(key);

  // Replace the root reducer
  const newRootReducer = createRootReducer();
  const persistedReducer = persistReducer(persistConfig, newRootReducer);
  storeRef.replaceReducer(persistedReducer);

  // Re-trigger persistence
  if (persistorRef) {
    persistorRef.persist();
  }

  return true;
}

/**
 * Get all currently injected reducers
 */
export function getInjectedReducers(): string[] {
  return Object.keys(asyncReducers);
}

/**
 * Check if a reducer is injected
 */
export function isReducerInjected(key: string): boolean {
  return key in asyncReducers;
}

/**
 * Clean up resources
 */
export function cleanup() {
  removeAllListeners();
  asyncReducers = {};
  reducerPersistConfigs = {};
  storeRef = null;
  persistorRef = null;
}
